import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import {
  CTP_ELGIBILITY_DETAILS,
  WGS_FEATURE_CODE,
  WGS_RETAIL_CC_BILLED,
  WGS_USER_ELIGIBLE_REASON_CODE,
  WGS_CTP_UPSELL_OFFER_TYPE,
  WGS_DTC_GROUP_TYPE,
} from 'utils/constants';
import { isPastSetupWindow } from 'utils';
import { getFromBrandConfig } from 'brand-config';
import {
  selectCompanyType,
  selectOfferBillingType,
  selectOfferPlanFeatures,
} from 'services/offer/offerSelectors';
import { selectHasWhiteGloveService } from 'services/session/sessionSelectors';
import { selectUserSince } from 'services/user/userSelectors';

const selectCtpState = (state: RootState) => state.ctp;

export const selectIsExpiryNoticeModalClosed = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.isExpiryNoticeModalClosed,
);

export const selectIsFinishAccountSetupModalClosed = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.isFinishAccountSetupModalClosed,
);

export const selectConversionEligibility = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.conversionEligibility,
);

export const selectEligibilityReasonCode = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.eligibility?.reasonCode,
);

export const selectIsConversionEligibilityDetailsExist = createSelector(
  [selectConversionEligibility],
  (conversionEligibility) => conversionEligibility !== null,
);

export const selectIsEligibilityDetailsExist = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.eligibility !== null,
);

export const selectAvailableOffers = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.availableOffers ?? [],
);

export const selectAvailableCtpPlan = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.availableCtpPlan,
);

export const selectIsEligibleForCtp = createSelector(
  [selectConversionEligibility, selectAvailableCtpPlan],
  (conversionEligibility, availableCtpPlan) =>
    (conversionEligibility?.eligible ?? false) && !!availableCtpPlan,
);

export const selectBillingAccountDetails = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.billingAccountInfo,
);

export const selectIsBillingAccountDetailsExist = createSelector(
  [selectBillingAccountDetails],
  (billingAccountDetails) => billingAccountDetails !== null,
);

export const selectIsAvailableOffersFetched = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.isAvailableOffersFetched,
);

// White Glove Service upsell banner gating — ports Angular's dashboard-carousel
// condition (dashboard-carousel.component.ts `shouldShowWgsUpsell`):
//
//   ( (currentProduct.group.type === 'DTC' && offer.billingType === RETAIL_CC_BILLED)
//     || isEligibleToShownCompleteSetupInfo(user.createdAt) )   // outer gate
//   && current product lacks id_theft_resolution
//   && some available offer plan has id_theft_resolution
//   && ( productChangeEligibility.reasonCode === 'USER_ELIGIBLE'
//        || offerChangeConfiguration.type === 'ctp upsell' )
//
// Available offers + product-change eligibility live in the ctp slice (fetched at
// bootstrap via useSessionInitialSetup).
export const selectIsEligibleForWhiteGloveBanner = createSelector(
  [
    selectHasWhiteGloveService,
    selectAvailableOffers,
    selectIsAvailableOffersFetched,
    selectCompanyType,
    selectOfferBillingType,
    selectOfferPlanFeatures,
    selectEligibilityReasonCode,
    selectUserSince,
  ],
  (
    hasWhiteGloveService,
    availableOffers,
    isAvailableOffersFetched,
    companyType,
    billingType,
    currentPlanFeatures,
    eligibilityReasonCode,
    userSince,
  ) => {
    if (!isAvailableOffersFetched || hasWhiteGloveService) {
      return false;
    }

    const isWithinSetupOrRetailWindow =
      (companyType === WGS_DTC_GROUP_TYPE &&
        billingType === WGS_RETAIL_CC_BILLED) ||
      isPastSetupWindow(userSince);

    if (!isWithinSetupOrRetailWindow) {
      return false;
    }

    const currentProductHasWgs =
      currentPlanFeatures?.includes(WGS_FEATURE_CODE) ?? false;
    if (currentProductHasWgs) {
      return false;
    }

    const isUserEligible =
      eligibilityReasonCode === WGS_USER_ELIGIBLE_REASON_CODE;

    return availableOffers.some(
      (o) =>
        o.plan?.features?.includes(WGS_FEATURE_CODE) &&
        (isUserEligible ||
          o.offerChangeConfiguration?.type === WGS_CTP_UPSELL_OFFER_TYPE),
    );
  },
);

export const selectIsEligibleForFinishAccountSetup = createSelector(
  [
    selectCompanyType,
    selectIsBillingAccountDetailsExist,
    selectBillingAccountDetails,
    selectIsEligibleForCtp,
  ],
  (
    companyType,
    isBillingAccountDetailsExist,
    billingAccountInfo,
    isEligibleForCtp,
  ) =>
    getFromBrandConfig('finishSetup') &&
    companyType === 'breach' &&
    isBillingAccountDetailsExist &&
    !billingAccountInfo?.defaultPaymentMethodId &&
    !isEligibleForCtp,
);

export const selectSubscriptionDetails = createSelector(
  [selectCtpState],
  (ctpState) => ctpState.subscriptionInfo,
);

export const selectIsSubscriptionDetailsExist = createSelector(
  [selectSubscriptionDetails],
  (subscriptionInfo) => subscriptionInfo !== null,
);

export const selectIsEligibleForCtpOptIn = createSelector(
  [
    selectCompanyType,
    selectIsBillingAccountDetailsExist,
    selectBillingAccountDetails,
    selectIsEligibleForCtp,
    selectAvailableCtpPlan,
    selectConversionEligibility,
  ],
  (
    companyType,
    isBillingAccountDetailsExist,
    billingAccountInfo,
    isEligibleForCtp,
    availableCtpPlan,
    conversionEligibility,
  ) =>
    isEligibleForCtp ||
    (getFromBrandConfig('finishSetup') &&
      companyType === 'breach' &&
      !!availableCtpPlan &&
      isBillingAccountDetailsExist &&
      (!billingAccountInfo?.defaultPaymentMethodId ||
        (!billingAccountInfo?.autoRenew &&
          conversionEligibility?.reasonCode !==
            CTP_ELGIBILITY_DETAILS.pendingCtp))),
);
